<template>
    <div>
        <rkm-form
            v-if="model"
            ref="RkmForm"
            :show-legend="model.items.length > 0"
        >
            <rkm-area type="fieldset" title="Dados da receita">
                <rkm-row>
                    <rkm-col cols="9">
                        <rkm-form-input-autocomplete
                            :disabled="disableField()"
                            :repository="repositories.usuario"
                            :requestQueryStringParams="{ with_not_regularized: true }"
                            :searchFields="fields.autocomplete.usuario"
                            :validations="validations.medicoSolicitante"
                            label="Médico solicitante"
                            placeholder="Digite o nome ou número do conselho profissional"
                            v-model="model.medicoSolicitante"
                            text-field="cidadaoNome"
                        >
                            <rkm-input-group-append slot="append">
                                <rkm-button
                                    @click="$refs.ModalUsuarioSimplificado.open()"
                                    color="primary"
                                    title="Cadastrar novo usuário"
                                >
                                    <rkm-icon icon="plus" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input-autocomplete>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :disabled="disableField()"
                            :max-date="form.dataAtual"
                            :validations="validations.dataReceita"
                            label="Data da receita"
                            right
                            v-model="model.dataReceita"
                        />
                    </rkm-col>

                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :disabled="disableField()"
                            :validations="validations.numeroNotificacao"
                            label="Número da notificação"
                            number
                            v-model="model.numeroNotificacao"
                        />
                    </rkm-col>

                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :disabled="disableField()"
                            :repository="repositories.cid"
                            :searchFields="fields.autocomplete.cid"
                            label="CID10"
                            v-model="model.cid"
                        >
                            <template v-slot:itemLabel="{ item }">
                                {{ item.codigo | formatCid }} - {{ item.nome }}
                            </template>
                        </rkm-form-input-autocomplete>
                    </rkm-col>

                    <rkm-col cols="2" class="mt-1">
                        <rkm-form-radio-group
                            :disabled="disableField()"
                            label="Tipo da receita"
                            v-model="model.isReceitaSus"
                        >
                            <rkm-form-radio :value="true">SUS</rkm-form-radio>
                            <rkm-form-radio :value="false">Outras</rkm-form-radio>
                        </rkm-form-radio-group>
                    </rkm-col>

                    <rkm-col cols="2" class="mt-1">
                        <rkm-form-radio-group
                            :disabled="model.items.length > 0 || disableField()"
                            label="Remédio em Casa"
                            v-model="model.isRemedioEmCasa"
                        >
                            <rkm-form-radio :value="false">Não</rkm-form-radio>
                            <rkm-form-radio :value="true">Sim</rkm-form-radio>
                        </rkm-form-radio-group>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            label="Observações"
                            v-model="model.observacao"
                            :validations="validations.observacao"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </rkm-form>

        <rkm-area type="fieldset" title="Adicionar item">
            <rkm-row v-if="model.items.length > 0 || !disableField()">
                <rkm-col>
                    <FormItem
                        :form="form"
                        :model="model"
                        :is-pendente="isPendente"
                        @save="handleSave"
                        ref="Form"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area type="fieldset" :title="`Item da receita (${ model.items.length })`">
            <rkm-row>
                <rkm-col>
                    <ListItem
                        ref="ListItem"
                        @add-item-watchers="addItemWatchers"
                        @remove-item-watchers="removeItemWatchers"
                        @save="handleSaveItem"
                        :form="form"
                        :model="model"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalUsuarioSimplificado
            ref="ModalUsuarioSimplificado"
            @save="setMedicoSolicitante"
        />

        <ModalSolicitacaoMedicamento
            :solicitacoes-medicamentos="form.solicitacoesMedicamentos"
            @click-to-dispensar="loadSolicitacaoMedicamentos"
            ref="ModalSolicitacaoMedicamento"
        />
    </div>
</template>

<script>
// Components
import ModalUsuarioSimplificado from '@/components/cadastro/usuario/components/cadastro-simplificado/Modal'
import ListItem from './item/List'
import FormItem from './item/Form'
import ModalSolicitacaoMedicamento from './components/solicitacao-medicamento/Modal'

// Filters.
import formats from '@/filters/formats'

// Mixins
import { repository, object, data } from '@/mixins'

// Moment
import moment from 'moment'

// Utils
import functions from '@/utils/farmacia/functions'

export default {
    components: { ListItem, FormItem, ModalUsuarioSimplificado, ModalSolicitacaoMedicamento },
    computed: {
        fields() {
            return {
                autocomplete: {
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                    cid: [{ key: "nome", searchable: true }],
                }
            }
        },
        validations() {
            return {
                medicoSolicitante: { required: true },
                dataReceita: { required: true },
                observacao: { min: 3, max:300 },
                numeroNotificacao: { max: 8 },
            }
        }
    },
    created() {

        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }

        if (this.model) {

            this.defaultItem = this.clone(this.model.item)
        }
    },
    data: () => ({
        defaultItem: null,
        others: { moment },
    }),
    filters: {
        ...formats,
    },
    methods: {
        ...functions,
        setMedicoSolicitante(usuario) {
            this.model.medicoSolicitante = {
                id: usuario.id,
                cidadaoNome: usuario.cidadao.nome
            }
        },
        disableField() {

            return this.model && Number(this.model.id) > 0
        },
        validate() {

            return this.$refs.RkmForm.validate()
        },
        handleSaveItem() {

            this.model.item = this.clone(this.defaultItem)
        },
        save() {

            if(this.model.items.length > 0) {
                this.$refs.RkmForm.validate()
                    .then(valid => {

                        if (!valid) return

                        this.$emit('save')
                    })
            } else {
                this.$emit('save')
            }
        },
        addItemWatchers() {

            this.$refs.Form.addWatchers()
        },
        removeItemWatchers() {

            this.$refs.Form.removeWatchers()
        },
        editItem(item) {

            this.$refs.ListItem.edit(item)
        },
        handleSave(item) {

            const itemClone = this.clone(item)

            itemClone.proximaRetirada = this.getFormatedDate(item.proximaRetirada, 'DD/MM/YYYY', 'YYYY-MM-DD')
            itemClone.dispensadoEm = this.getFormatedDate(item.dispensadoEm, 'DD/MM/YYYY', 'YYYY-MM-DD')

            const hasMaterial = this.model.items.some(i => i.material.id == itemClone.material.id && i.id != item.id)
            if (hasMaterial) {

                this.showWarningNotification("Este item já está na lista!")

                return
            }

            if (itemClone.id) {

                const idx = this.model.items.findIndex(i => i.id === itemClone.id)
                Object.assign(this.model.items[idx], itemClone)

            } else {

                itemClone.id = `${new Date().getTime()}-receita-item`
                this.model.items.push(itemClone)
            }

            this.handleSaveItem()
        },
        loadSolicitacaoMedicamentos(solicitacao) {

            this.model.ambulatorioPrescricaoMedicamentoId = solicitacao.prescricaoId

            this.model.medicoSolicitante = {
                id: solicitacao.usuarioId,
                cidadaoNome: solicitacao.usuarioNome,
            }

            this.model.dataReceita = this.others.moment(solicitacao.dataHora).toDate()

            if (solicitacao.cidId) {

                this.model.cid = {
                    id: solicitacao.cidId,
                    codigo: solicitacao.cidCodigo,
                    nome: solicitacao.cidNome,
                }
            }

            solicitacao.itens.forEach(item => {

                const itemData = {
                    id: `${new Date().getTime()}${item.materialId}-receita-item`,
                    receitaId: null,
                    materialId: item.materialId,
                    material: {
                        id: item.materialId,
                        apresentacaoUnidadeMedidaNome: item.materialApresentacaoUnidadeMedidaNome,
                        quantidade: item.materialQuantidade,
                        descricaoCompleta: item.materialDescricaoCompleta,
                    },
                    estoque: item.estoque,
                    dispensadoEm: this.others.moment(this.form.dataAtual, 'DD/MM/YYYY').toDate(),
                    quantidade: item.dose,
                    intervaloTempo: item.intervalo,
                    intervaloMedidaTempoId: item.intervaloMedidaTempoId,
                    duranteTempo: item.duracao,
                    prescrito: item.prescrito,
                    entregue: item.entregue,
                    escolheuEntradaItemManualmente: item.escolheuEntradaItemManualmente,
                    entradaItens: item.entradaItens,
                    possuiEntradaComLote: item.possuiEntradaComLote,
                    observacao: item.recomendacoes,
                    supervisorId: null,
                    continuo: item.isUsoContinuo,
                    programa: item.programa,
                    proximaRetirada: null
                }

                const itemCloned = this.clone(itemData)

                if (!itemCloned.continuo && itemCloned.quantidade && itemCloned.intervaloTempo && !itemCloned.duranteTempo) {

                    itemCloned.continuo = true
                }

                const proximaRetirada = this.getDateProximaRetirada(itemCloned)

                if (proximaRetirada) {

                    itemData.proximaRetirada = proximaRetirada.format('DD/MM/YYYY')
                }

                itemData.dispensadoEm = this.others.moment(itemData.dispensadoEm).format('DD/MM/YYYY')

                this.model.items.push(itemData)
            })
        },
        openModalSolicitacoes() {

            this.$nextTick(() => this.$refs.ModalSolicitacaoMedicamento.open())
        }
    },
    mixins: [repository, object, data],
    props: {
        form: { type: Object, default: null },
        model: { type: Object, default: null },
        isPendente: { type: Boolean, default: false}
    }
}
</script>